import React from 'react';
import { Link } from 'react-router-dom';
import type { RecentDocumentItem } from '../../api/documents';

interface InFlightPipelineStripProps {
  items: RecentDocumentItem[];
}

/**
 * Map a backend pipeline_status token to a short human Romanian
 * label + a progress hint (0..1). Mirrors the vocabulary used in
 * `MemberDocumentCard` so the same stage labels surface across the
 * app. Anything we don't recognise gets a generic "Procesare" label.
 */
function stageDescriptor(pipeline: string | null | undefined): {
  label: string;
  progress: number;
} {
  switch (pipeline) {
    case 'queued':
      return { label: 'În coadă', progress: 0.1 };
    case 'processing_started':
      return { label: 'Procesare inițiată', progress: 0.2 };
    case 'loading_images':
      return { label: 'Încărcare imagini', progress: 0.35 };
    case 'ocr_extraction':
      return { label: 'Extragere text (OCR)', progress: 0.55 };
    case 'processing':
      return { label: 'Procesare avansată', progress: 0.65 };
    case 'summary_generating':
      return { label: 'Se generează rezumatul', progress: 0.8 };
    case 'classification_generating':
      return { label: 'Se clasifică documentul', progress: 0.92 };
    default:
      return { label: 'Procesare', progress: 0.5 };
  }
}

function formatFilenameShort(name: string, max: number = 40): string {
  if (!name) return 'Document fără nume';
  if (name.length <= max) return name;
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot < name.length - 8) return `${name.slice(0, max - 1)}…`;
  const base = name.slice(0, dot);
  const ext = name.slice(dot);
  const keep = Math.max(4, max - ext.length - 1);
  return `${base.slice(0, keep)}…${ext}`;
}

const IconSpinner = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    aria-hidden
    className="dash-inflight-spinner-svg"
  >
    <path d="M21 12a9 9 0 1 1-6.22-8.56" />
  </svg>
);

const InFlightPipelineStrip: React.FC<InFlightPipelineStripProps> = ({ items }) => {
  if (!items.length) return null;

  return (
    <section className="dash-inflight" aria-labelledby="dash-inflight-title">
      <div className="dash-inflight-head">
        <h2 className="dash-inflight-title" id="dash-inflight-title">
          <span className="dash-inflight-pulse" aria-hidden />
          În procesare acum
        </h2>
        <span className="dash-inflight-count">
          {items.length} {items.length === 1 ? 'document' : 'documente'}
        </span>
      </div>
      <div className="dash-inflight-track" role="list">
        {items.map((doc) => {
          const stage = stageDescriptor(doc.pipeline_status);
          const pct = Math.round(stage.progress * 100);
          return (
            <Link
              key={doc.id}
              to={`/documents/${doc.id}`}
              role="listitem"
              className="dash-inflight-card"
              aria-label={`${doc.original_filename} pentru ${doc.member_name} — ${stage.label}`}
            >
              <div className="dash-inflight-card-head">
                <span className="dash-inflight-stage">
                  <IconSpinner />
                  {stage.label}
                </span>
              </div>
              <div className="dash-inflight-card-name" title={doc.original_filename}>
                {formatFilenameShort(doc.original_filename)}
              </div>
              <div className="dash-inflight-card-member" title={doc.member_name}>
                {doc.member_name}
              </div>
              <div className="dash-inflight-bar" aria-hidden>
                <div
                  className="dash-inflight-bar-fill"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default InFlightPipelineStrip;
